import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserService } from '../../domain/user/service/user.service';

const AUTH_PATHS_TO_SKIP = ['/signin', '/signup', '/exists/', '/find/', '/reset/', '/confirmation/'];
const REISSUE_PATH = '/signin/reissue';
export const SESSION_HINT_KEY = 'hasSession';

let isRefreshing = false;
const refreshState$ = new BehaviorSubject<'idle' | 'success' | 'fail'>('idle');

function shouldSkip(url: string): boolean {
  if (url.includes(REISSUE_PATH)) return true;
  return AUTH_PATHS_TO_SKIP.some(path => url.includes(path));
}

// 로그인 흔적(hint). 새로고침 직후엔 currentUserValue 가 null 이라 "비로그인" 과 "토큰만료된 로그인" 을
// 구분할 수 없는데, hint 가 없으면 애초에 로그인한 적 없는 사용자이므로 reissue 시도 자체를 건너뛴다
// → 공개 페이지에서 /users/me 401 + /signin/reissue 401 이중 호출 제거. (실토큰은 HttpOnly 쿠키이므로 보안 무관.)
function hasSessionHint(): boolean {
  return !!localStorage.getItem(SESSION_HINT_KEY);
}

export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const userService = inject(UserService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || shouldSkip(req.url) || !hasSessionHint()) {
        return throwError(() => error);
      }
      // 새로고침 직후엔 BehaviorSubject 가 null 이라 "비로그인" 과 "토큰만료된 로그인" 을 구분 불가.
      // → reissue 는 항상 시도하되, 실패 시 페이지 이동은 원래 로그인 상태였을 때만.
      const wasLoggedIn = !!userService.currentUserValue;
      return handleUnauthorized(req, next, userService, router, wasLoggedIn);
    })
  );
};

function handleUnauthorized(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  userService: UserService,
  router: Router,
  wasLoggedIn: boolean
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshState$.next('idle');

    return userService.reissueToken().pipe(
      switchMap(() => {
        isRefreshing = false;
        refreshState$.next('success');
        return next(req);
      }),
      catchError(err => {
        isRefreshing = false;
        refreshState$.next('fail');
        userService.clearCurrentUser();
        localStorage.removeItem(SESSION_HINT_KEY); // refresh 도 만료 → 다음 요청부터 reissue 자체를 스킵
        // 원래 로그인 상태였던 경우만 강제 이동 (비로그인 페이지 진입 시 자동 복구 실패는 silent).
        if (wasLoggedIn) {
          router.navigate(['/signin']);
        }
        return throwError(() => err);
      })
    );
  }

  return refreshState$.pipe(
    filter(state => state !== 'idle'),
    take(1),
    switchMap(state =>
      state === 'success'
        ? next(req)
        : throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Token refresh failed' }))
    )
  );
}
